import { useEffect, useRef, useState } from "react";
import PixelIcon from "./PixelIcon";

/** Where the icon sits relative to the mouse pointer (px). */
const OFFSET_X = 14;
const OFFSET_Y = 16;

/**
 * CURSOR ICON — a little pixel icon that follows the mouse over some links
 * ---------------------------------------------------------------------------
 * Give any link a `data-cursor-icon` attribute naming an icon from
 * components/PixelIcon.js, and hovering it shows that icon beside the
 * pointer:
 *
 *   <a href="mailto:…" data-cursor-icon="mail">Send me an email</a>
 *
 * Only used on devices with a mouse (see Layout.js). Works on every page.
 */
export default function CursorIcon() {
  const [icon, setIcon] = useState(null); // name of the icon showing, or null
  const boxRef = useRef(null);

  useEffect(() => {
    let frame = null;
    let x = 0;
    let y = 0;

    const onMove = (e) => {
      x = e.clientX;
      y = e.clientY;
      // Which icon (if any) does the element under the pointer ask for?
      const target = e.target.closest?.("[data-cursor-icon]");
      setIcon(target ? target.dataset.cursorIcon : null); // no-op if same

      // Move the icon once per frame, directly (no React re-render)
      if (frame === null) {
        frame = requestAnimationFrame(() => {
          frame = null;
          if (boxRef.current) {
            boxRef.current.style.transform = `translate(${x + OFFSET_X}px, ${y + OFFSET_Y}px)`;
          }
        });
      }
    };
    const onLeave = () => setIcon(null); // pointer left the window

    window.addEventListener("mousemove", onMove, { passive: true });
    document.documentElement.addEventListener("mouseleave", onLeave);
    return () => {
      window.removeEventListener("mousemove", onMove);
      document.documentElement.removeEventListener("mouseleave", onLeave);
      if (frame !== null) cancelAnimationFrame(frame);
    };
  }, []);

  return (
    <div className="cursor-icon" ref={boxRef} aria-hidden="true">
      {/* `key` restarts the little pop-in each time the icon changes */}
      {icon && (
        <span className="cursor-icon__inner" key={icon}>
          <PixelIcon name={icon} />
        </span>
      )}
    </div>
  );
}
